import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Modal } from '@components/Modal';
import { Spacer } from '@components/Spacer';
import { Loader } from '@components/Loader';
import { DatePicker } from '@components/DatePicker';
import { CloseIcon } from '@icons/CloseIcon';
import { CalendarIcon } from '@icons/CalendarIcon';
import { DownloadProcessingUsageCSV } from '@account_module/components/DownloadProcessingUsageCSV';
import { AudioProcessingReportTable } from '@account_module/components/AudioProcessingReportTable';
import { useAudioProcessingTracking } from '@account_module/hooks/useAudioProcessingTracking';
import { CurrentMonthAudioProcessingReport } from '@account_module/components/CurrentMonthAudioProcessingReport';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import {
  AudioProcessingReportType,
  AudioProcessingIntervalType,
  AudioProcessingReportResponseType,
} from '@account_module/types/AudioProcessing';
import {
  convertSecondsToHMS,
  dateWithoutTimezone,
  extractDateFromISOString,
  getDateSixMonthsEarlier,
} from '@utils/dateUtils';
import { PROCESSING_INTERVAL } from '@account_module/constants/audioProcessing';

type ReportStringPropsType = {
  isLoading: boolean;
  audio_count: number;
  processingDuration: number;
};

const RelativeContainer = tw.div`relative`;
const DatePickerTrigger = tw.div`text-xs text-text-and-icon hover:underline cursor-pointer inline-flex justify-start items-center h-full underline`;
const UsageCopy = tw.div`flex items-center justify-start gap-2 [&>span]:text-xs [&>span]:text-text-and-icon`;
const ModalInnerContainer = tw.div`bg-neutral-background rounded-lg shadow-dropdown h-full`;
const ModalHeader = tw.div`flex items-center justify-between border-b border-solid border-gray-1 px-4 py-2`;
const ModalBody = tw.div`p-4`;
const ModalTitle = tw.h3`text-[16px] text-text-and-icon text-left font-semibold`;
const DatePickerWrapper = tw.div`relative z-50`;
const DatePickerHolder = tw.div`absolute top-[1rem] right-0`;
const DatePickerToggler = tw.div`text-text-and-icon text-sm my-2 underline cursor-pointer`;
const FlexStart = tw.div`flex items-center justify-start gap-2`;
const FlexBetween = tw.div`w-full flex items-center justify-between`;
const FlexEnd = tw.div`w-full flex items-center justify-end`;
const SemiboldSpan = tw.span`font-semibold text-text-and-icon`;
const StyledSpan = tw.span`text-xs text-text-and-icon`;
const ReportStringContainer = tw.div`flex items-center justify-start gap-2`;

const modalStartDate = getDateSixMonthsEarlier(new Date());
const _modalStartDate = new Date(modalStartDate.getFullYear(), modalStartDate.getMonth(), 1);

const modalDefaultValue = {
  start_date: _modalStartDate,
  end_date: new Date(),
  interval: PROCESSING_INTERVAL.MONTH,
};

const INTERVAL_STRING_MAPPER = {
  [PROCESSING_INTERVAL.DAY]: 'daily',
  [PROCESSING_INTERVAL.WEEK]: 'weekly',
  [PROCESSING_INTERVAL.MONTH]: 'monthly',
  [PROCESSING_INTERVAL.YEAR]: 'yearly',
};

const ReportString = ({
  isLoading,
  audio_count,
  processingDuration,
}: ReportStringPropsType): JSX.Element => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <ReportStringContainer>
        <Loader size='xs' theme='dark' tw='m-0' />
        <StyledSpan>|</StyledSpan>
        <Loader size='xs' theme='dark' tw='m-0' />
      </ReportStringContainer>
    );
  }

  if (audio_count === 0 && processingDuration === 0) {
    return (
      <StyledSpan>{t('account_module.settings_page.audio_processing_report.no_data')}</StyledSpan>
    );
  }

  return (
    <ReportStringContainer>
      <StyledSpan>
        {t('account_module.settings_page.audio_processing_report.files_count', {
          count: audio_count,
        })}
      </StyledSpan>
      <StyledSpan>|</StyledSpan>
      <StyledSpan>{convertSecondsToHMS(processingDuration)}h</StyledSpan>
    </ReportStringContainer>
  );
};

export const AudioProcessingUsage = () => {
  const { t } = useTranslation();
  const datePickerRef = createRef<ReactDatePicker>();
  const datePickerContainer = useRef<HTMLDivElement>(null);
  const [isDatePickerVisible, setIsDatepickerVisible] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  useOnClickOutside(datePickerContainer, () => setIsDatepickerVisible(false));

  const [paramsState, setParamsState] = useState<{
    [key: string]: Date | string | null | AudioProcessingIntervalType;
  }>(modalDefaultValue);

  const [urlParams, setUrlParams] = useState<string | null>(null);
  const { data: processingUsageData, isLoading, error } = useAudioProcessingTracking(urlParams);
  const onChange = (dates: Array<Date | null>) => {
    const [start, end] = dates;

    const _state = {
      start_date: start,
      end_date: end,
    };

    setParamsState((paramsState) => ({ ...paramsState, ..._state }));

    if (!!start && !!end) {
      setIsDatepickerVisible(false);
    }
  };

  const processingSum = useMemo<AudioProcessingReportType>(() => {
    return processingUsageData?.reduce(
      (acc: AudioProcessingReportType, item: AudioProcessingReportResponseType) => ({
        audio_duration_sec: (acc.audio_duration_sec += item.audio_duration_sec),
        audio_count: (acc.audio_count += item.audio_count),
      }),
      { audio_duration_sec: 0, audio_count: 0 }
    );
  }, [processingUsageData]);

  useEffect(() => {
    if (!paramsState?.end_date || !paramsState?.start_date) return;

    const startDate = new Date(paramsState.start_date);
    const endDate = new Date(paramsState?.end_date);

    setUrlParams(
      new URLSearchParams({
        start_date: extractDateFromISOString(dateWithoutTimezone(startDate)) as string,
        end_date: extractDateFromISOString(dateWithoutTimezone(endDate)) as string,
        interval: (paramsState?.interval as string) || PROCESSING_INTERVAL.MONTH,
      }).toString()
    );
  }, [paramsState, setParamsState]);

  const dateRangeStart = extractDateFromISOString(
    dateWithoutTimezone(new Date(paramsState?.start_date as string))
  );
  const dateRangeEnd = paramsState?.end_date
    ? extractDateFromISOString(dateWithoutTimezone(new Date(paramsState?.end_date as string)))
    : '-';

  const isError = useMemo(
    () => error || (!isLoading && !processingUsageData),
    [error, isLoading, processingUsageData]
  );

  if (isError) {
    return (
      <RelativeContainer>
        <UsageCopy>
          <StyledSpan>{t('account_module.settings_page.audio_processing_report.error')}</StyledSpan>
        </UsageCopy>
      </RelativeContainer>
    );
  }

  return (
    <RelativeContainer>
      <UsageCopy>
        <SemiboldSpan>
          {t('account_module.settings_page.audio_processing_report.total_processed_content')}
        </SemiboldSpan>
        <CurrentMonthAudioProcessingReport />
        <DatePickerTrigger onClick={() => setIsModalVisible(!isModalVisible)}>
          {t('account_module.settings_page.audio_processing_report.see_more')}
        </DatePickerTrigger>
      </UsageCopy>

      {isModalVisible && (
        <Modal onClose={() => setIsModalVisible(false)}>
          <ModalInnerContainer>
            <ModalHeader>
              <div>
                <ModalTitle>
                  {t('account_module.settings_page.audio_processing_report.modal_title')}
                </ModalTitle>
                <StyledSpan tw='text-text-and-icon-muted'>
                  {t('account_module.settings_page.audio_processing_report.modal_note')}
                </StyledSpan>
              </div>
              <button onClick={() => setIsModalVisible(false)}>
                <CloseIcon tw='fill-text-and-icon' stroke='none' />
              </button>
            </ModalHeader>
            <Spacer direction='vertical' size='md' />
            <ModalBody>
              <FlexBetween>
                <FlexStart>
                  <StyledSpan>
                    {t('account_module.settings_page.audio_processing_report.date_range')}
                  </StyledSpan>
                  <CalendarIcon tw='stroke-text-and-icon' />
                  <DatePickerToggler onClick={() => setIsDatepickerVisible(!isDatePickerVisible)}>
                    <StyledSpan>
                      {dateRangeStart} / {dateRangeEnd}
                    </StyledSpan>
                  </DatePickerToggler>
                  <DatePickerWrapper>
                    {isDatePickerVisible && (
                      <DatePickerHolder ref={datePickerContainer}>
                        <DatePicker
                          ref={datePickerRef}
                          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                          // @ts-expect-error
                          onChange={onChange}
                          selected={paramsState?.start_date}
                          startDate={paramsState?.start_date}
                          endDate={paramsState?.end_date}
                          selectsRange
                          inline
                        />
                      </DatePickerHolder>
                    )}
                  </DatePickerWrapper>
                </FlexStart>
              </FlexBetween>
              <FlexStart>
                <ReportString
                  isLoading={isLoading}
                  audio_count={processingSum?.audio_count}
                  processingDuration={processingSum?.audio_duration_sec}
                />
              </FlexStart>
              <Spacer direction='vertical' size='md' />
              <AudioProcessingReportTable
                params={paramsState}
                data={processingUsageData}
                isLoading={isLoading}
                onChange={(value) => setParamsState({ ...paramsState, interval: value })}
              />
              <Spacer size='md' direction='vertical' />
              <FlexEnd>
                <DownloadProcessingUsageCSV
                  data={processingUsageData}
                  tw='p-[0.5rem] ml-4 text-[12px]'
                  interval={
                    INTERVAL_STRING_MAPPER[paramsState.interval as AudioProcessingIntervalType]
                  }
                  fileName={`Sounder audio procesing report ${dateRangeStart} / ${dateRangeEnd}`}
                />
              </FlexEnd>
            </ModalBody>
          </ModalInnerContainer>
        </Modal>
      )}
    </RelativeContainer>
  );
};
